/**
 * AppliedFilters - Display and manage applied attribute filters
 */
import { ReactElement, useCallback } from 'react'
import { connect } from 'react-redux'
import { Flex, Tag, Button } from 'antd'
import { CloseCircleOutlined } from '@ant-design/icons'
import {
  removeAttributeValueFilter,
  clearAttributeValueFilters,
} from 'actions/search/filters'
import { getAttributeValueFilters } from 'selectors/search/filters'
import styles from './AppliedFilters.module.scss'

interface AppliedFiltersProps {
  appliedFilters: Array<{ attributeCode: string; value: string }>
}

interface ReduxState {
  appliedFilters: Array<{ attributeCode: string; value: string }>
}

const select = (): ReduxState => ({
  appliedFilters: getAttributeValueFilters(),
})

/**
 * AppliedFilters - Displays applied filters with remove functionality
 * Connected to Redux for autonomous state management
 */
const AppliedFilters = (props: AppliedFiltersProps): ReactElement => {
  const { appliedFilters } = props

  const handleRemoveFilter = useCallback(
    (attributeCode: string, value: string) => {
      removeAttributeValueFilter(attributeCode, value)
    },
    [],
  )

  const handleClearAll = useCallback(() => {
    clearAttributeValueFilters()
  }, [])

  return (
    <Flex align="flex-start" gap={8} wrap="wrap" className={styles.container} data-tour="applied-filters">
      <span className={styles.label}>Applied Filters:</span>
      {[...appliedFilters]
        .sort((a, b) => a.attributeCode.localeCompare(b.attributeCode))
        .map((filter) => {
          const fullText = `${filter.attributeCode} : ${filter.value}`
          return (
            <Tag
              key={`${filter.attributeCode}-${filter.value}`}
              closable
              onClose={() =>
                handleRemoveFilter(filter.attributeCode, filter.value)
              }
              color="blue"
              title={fullText}
              className={styles.tag}
            >
              <span className={styles.tagText}>{fullText}</span>
            </Tag>
          )
        })}
      {appliedFilters.length > 0 && (
        <Button
          size="small"
          type="link"
          icon={<CloseCircleOutlined />}
          onClick={handleClearAll}
          className={styles.clearButton}
        >
          Clear all
        </Button>
      )}
    </Flex>
  )
}

export default connect(select)(AppliedFilters)
